import { type RaRecord, required, useRecordContext } from 'ra-core';
import { useFormContext, useWatch } from 'react-hook-form';
import { AutocompleteArrayInput } from '../../components/admin/autocomplete-array-input';
import { ReferenceArrayInput } from '../../components/admin/reference-array-input';
import { SelectInput } from '../../components/admin/select-input';
import {
  RULE_TYPES,
  OPERATORS,
  type RuleType,
  type Operator,
} from './userGroupRuleConstants';

const ruleTypeChoices = [
  { id: RULE_TYPES.INCLUDE_ALL, name: 'Include all members' },
  { id: RULE_TYPES.BY_JOB_TITLE, name: 'By job title' },
  { id: RULE_TYPES.BY_UNITS, name: 'By units' },
];

const createRuleTypeChoices = ruleTypeChoices.filter(
  (choice) => choice.id !== RULE_TYPES.INCLUDE_ALL
);

const jobTitleOperatorChoices = [
  { id: OPERATORS.IN, name: 'Is one of' },
  { id: OPERATORS.NOT_IN, name: 'Is not one of' },
];

const unitOperatorChoices = [
  { id: OPERATORS.ANY_IN, name: 'Has any of' },
  { id: OPERATORS.NOT_ANY_IN, name: 'Has none of' },
  { id: OPERATORS.ALL_IN, name: 'All are in' },
  { id: OPERATORS.NOT_ALL_IN, name: 'Some are not in' },
];

interface UserGroupFormData {
  name: string;
  ruleType: RuleType | '';
  operator: Operator | '';
  jobTitleIds: number[];
  unitIds: number[];
}

/**
 * Transforms the flat form data into the API payload shape.
 */
export const transformUserGroupData = (data: UserGroupFormData) => {
  const { name, ruleType, operator, jobTitleIds, unitIds } = data;

  if (!ruleType) {
    return { name, rules: null };
  }

  if (ruleType === RULE_TYPES.INCLUDE_ALL) {
    return {
      name,
      rules: {
        version: 1 as const,
        dependencies: ['user.id'] as const,
        value: { type: RULE_TYPES.INCLUDE_ALL },
      },
    };
  } else if (ruleType === RULE_TYPES.BY_JOB_TITLE) {
    return {
      name,
      rules: {
        version: 1 as const,
        dependencies: [RULE_TYPES.BY_JOB_TITLE] as const,
        value: {
          type: RULE_TYPES.BY_JOB_TITLE,
          operator,
          values: jobTitleIds,
        },
      },
    };
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  } else if (ruleType === RULE_TYPES.BY_UNITS) {
    return {
      name,
      rules: {
        version: 1 as const,
        dependencies: [RULE_TYPES.BY_UNITS] as const,
        value: {
          type: RULE_TYPES.BY_UNITS,
          operator,
          values: unitIds,
        },
      },
    };
  }
};

export const userGroupRecordToFormValues = (
  record?: RaRecord
): Partial<UserGroupFormData> => {
  const ruleValue = record?.rules?.value;

  const jobTitleIds =
    ruleValue?.type === RULE_TYPES.BY_JOB_TITLE ? ruleValue.values : undefined;
  const unitIds =
    ruleValue?.type === RULE_TYPES.BY_UNITS ? ruleValue.values : undefined;

  return {
    ruleType: ruleValue?.type ?? userGroupCreateDefaultValues.ruleType,
    operator: ruleValue?.operator ?? userGroupCreateDefaultValues.operator,
    jobTitleIds: jobTitleIds ?? userGroupCreateDefaultValues.jobTitleIds,
    unitIds: unitIds ?? userGroupCreateDefaultValues.unitIds,
  };
};

export const userGroupCreateDefaultValues: Partial<UserGroupFormData> = {
  ruleType: '',
  operator: '',
  jobTitleIds: [],
  unitIds: [],
};

const requiredWhenJobTitle = [
  required(),
  (value: unknown, allValues: UserGroupFormData) => {
    if (allValues.ruleType !== RULE_TYPES.BY_JOB_TITLE) return undefined;
    if (Array.isArray(value) && value.length === 0) {
      return 'ra.validation.required';
    }
    return undefined;
  },
];

const requiredWhenUnits = [
  required(),
  (value: unknown, allValues: UserGroupFormData) => {
    if (allValues.ruleType !== RULE_TYPES.BY_UNITS) {
      return undefined;
    }
    if (Array.isArray(value) && value.length > 0) {
      return undefined;
    }

    return 'ra.validation.required';
  },
];

export const UserGroupRulesInputs = () => {
  const record = useRecordContext();
  const isEditMode = !!record;
  const ruleType = useWatch<UserGroupFormData>({ name: 'ruleType' });
  const { setValue, getValues } = useFormContext<UserGroupFormData>();

  return (
    <>
      <SelectInput
        source="ruleType"
        label="Rule type"
        choices={
          isEditMode && ruleType === RULE_TYPES.INCLUDE_ALL
            ? ruleTypeChoices
            : createRuleTypeChoices
        }
        disabled={isEditMode && ruleType === RULE_TYPES.INCLUDE_ALL}
        emptyText="No rules"
        onChange={() => {
          // Reset the operator, as the operator is different for different rule types
          const newType = getValues('ruleType');

          if (newType === RULE_TYPES.BY_JOB_TITLE) {
            setValue('operator', 'IN');
          } else if (newType === RULE_TYPES.BY_UNITS) {
            setValue('operator', 'ANY_IN');
          } else {
            setValue('operator', '');
          }
        }}
      />
      {ruleType === RULE_TYPES.BY_JOB_TITLE ? (
        <>
          <SelectInput
            source="operator"
            label="Operator"
            choices={jobTitleOperatorChoices}
            validate={required()}
          />
          <ReferenceArrayInput
            source="jobTitleIds"
            reference="jobTitles"
            perPage={999}
            sort={{ field: 'name', order: 'ASC' }}
          >
            <AutocompleteArrayInput
              optionText="name"
              label="Job Titles"
              validate={requiredWhenJobTitle}
              filterToQuery={(q: string) => ({ name: q })}
            />
          </ReferenceArrayInput>
        </>
      ) : ruleType === RULE_TYPES.BY_UNITS ? (
        <>
          <SelectInput
            source="operator"
            label="Operator"
            choices={unitOperatorChoices}
            validate={required()}
          />
          <ReferenceArrayInput
            source="unitIds"
            reference="units"
            perPage={999}
            sort={{ field: 'name', order: 'ASC' }}
          >
            <AutocompleteArrayInput
              optionText="name"
              label="Units"
              validate={requiredWhenUnits}
              filterToQuery={(q: string) => ({ name: q })}
            />
          </ReferenceArrayInput>
        </>
      ) : null}
    </>
  );
};
